import type jsPDF from 'jspdf';
import {
  PDFQuoteData,
  resolveDocumentTitle,
  resolveDocumentSubtitle,
  extraFieldEntries,
  formatQuoteDate,
  calcValidityDate,
  formatCurrency,
  clientDocumentLabel,
  pdfQuoteTotals,
  formatPaymentMethods,
  diagnosisEntries,
  assetEntries,
  serviceOrderInfoEntries,
  showsPdvClientSignature,
} from './shared';
import { quoteItemLineTotal } from '@/lib/quoteTotals';
import { unitShortLabel } from '@/lib/units';
import { formatQuantityDisplay } from '@/lib/quantity';
import { numeroParaReais } from '@/lib/valorExtenso';
import type { ThermalSpec } from '@/lib/thermalPaper';

/**
 * Renderer ÚNICO da bobina térmica (58 mm / 80 mm).
 *
 * Um só renderer para os quatro documentos (orçamento, pedido/PDV, recibo,
 * ordem de serviço) porque em 48–72 mm de largura não sobra espaço para
 * diferenciação visual: sem cor, sem logo, sem foto, tudo em preto e branco de
 * 1 bit. `data.template` é IGNORADO de propósito — os 9 templates colapsariam
 * no mesmo cupom.
 *
 * ─── DUAS INVARIANTES QUE O RESTO DEPENDE ────────────────────────────────────
 *
 * 1. **Nunca leia `doc.internal.pageSize.getHeight()`.** A bobina é contínua;
 *    quem chama mede a altura rodando este renderer numa página gigante e
 *    depois cria a página no tamanho certo (`generateQuotePDF`). Se o layout
 *    dependesse da altura da página, as duas passadas divergiriam e a segunda
 *    cortaria conteúdo — sem erro nenhum. Há guard de varredura para isto.
 *
 * 2. **Nada de não-determinismo.** Sem `Date.now()`, sem random. É por isso que
 *    o QR do PIX chega PRONTO em `opts.pixQrDataUrl`: `renderPixSection` (o
 *    caminho A4) monta um `txId` com `Date.now()`, o que faria as duas passadas
 *    produzirem QRs de tamanhos possivelmente diferentes.
 *
 * Por essas duas razões a função é SÍNCRONA: sem `await`, não existe a classe
 * de bug do `await` esquecido que fecha o documento antes da imagem entrar.
 *
 * Devolve o `y` final (mm), que é a altura de conteúdo usada.
 */
export interface ThermalRenderOptions {
  spec: ThermalSpec;
  /** QR do PIX já gerado (data URL PNG) ou `null`. Resolvido UMA vez pelo chamador. */
  pixQrDataUrl?: string | null;
}

const BLACK: [number, number, number] = [0, 0, 0];
/** Cinza médio: no térmico vira meio-tom pontilhado; usado só em texto acessório. */
const GRAY: [number, number, number] = [90, 90, 90];

export function render(
  doc: jsPDF,
  data: PDFQuoteData,
  opts: ThermalRenderOptions,
): number {
  const { spec } = opts;
  const { margin } = spec;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;
  const left = margin;
  const right = pageWidth - margin;
  const center = pageWidth / 2;
  const narrow = spec.paperWidth < 70;

  /**
   * O recibo é um documento FECHADO: comprova um pagamento já feito. Por isso
   * ele não leva validade, referência, formas aceitas, garantia, observações
   * gerais nem termos — exatamente como o recibo A4 (`recibo.ts`), que também
   * não imprime nenhum desses campos. Sem este corte, um recibo herdaria as
   * seções genéricas do orçamento e sairia dizendo "Válido até" e "Garantia" na
   * bobina, divergindo do PDF do mesmo recibo.
   */
  const isReceipt = data.documentType === 'recibo';

  let y = margin + 2;

  // -------------------------------------------------------------------------
  // Primitivas de desenho. Todas AVANÇAM `y` (jsPDF não tem layout — armadilha
  // #13: cada elemento é coordenada absoluta e o acúmulo é manual).
  // -------------------------------------------------------------------------
  const setFont = (size: number, style: 'normal' | 'bold' = 'normal', color = BLACK) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', style);
    doc.setTextColor(...color);
  };

  /** Altura de linha proporcional ao corpo — 1,35× é legível a 203 dpi. */
  const lh = (size: number) => size * 0.3528 * 1.35;

  const writeCentered = (
    text: string,
    size: number,
    style: 'normal' | 'bold' = 'normal',
    color = BLACK,
  ) => {
    if (!text) return;
    setFont(size, style, color);
    const lines = doc.splitTextToSize(text, contentWidth) as string[];
    for (const line of lines) {
      y += lh(size);
      doc.text(line, center, y, { align: 'center' });
    }
  };

  const writeLeft = (
    text: string,
    size: number,
    style: 'normal' | 'bold' = 'normal',
    color = BLACK,
    indent = 0,
  ) => {
    if (!text) return;
    setFont(size, style, color);
    const lines = doc.splitTextToSize(text, contentWidth - indent) as string[];
    for (const line of lines) {
      y += lh(size);
      doc.text(line, left + indent, y);
    }
  };

  /**
   * Linha de duas colunas: rótulo à esquerda, valor colado à direita. É o
   * formato de toda linha de dinheiro do cupom — o olho do cliente desce pela
   * coluna da direita.
   *
   * ARMADILHA DA BOBINA ESTREITA: em 58 mm sobram 48 mm úteis, e um rótulo
   * comprido ("Descontos nos itens") mais o valor não cabem na mesma linha —
   * `doc.text` não avisa, ele simplesmente DESENHA UM POR CIMA DO OUTRO, e o
   * cupom sai com o valor ilegível. Por isso a largura é MEDIDA antes: quando
   * não cabe, o valor desce para uma linha própria alinhada à direita, em vez
   * de sobrepor. Wrap silencioso é aceitável; sobreposição não.
   */
  const writeRow = (
    label: string,
    value: string,
    size: number,
    style: 'normal' | 'bold' = 'normal',
    color = BLACK,
    indent = 0,
  ) => {
    setFont(size, style, color);
    const available = contentWidth - indent;
    const gap = 2;
    const valueWidth = doc.getTextWidth(value);

    if (doc.getTextWidth(label) + gap + valueWidth <= available) {
      y += lh(size);
      doc.text(label, left + indent, y);
      doc.text(value, right, y, { align: 'right' });
      return;
    }

    // Não coube: rótulo quebrado à esquerda, valor sozinho na linha seguinte.
    const labelLines = doc.splitTextToSize(label, available) as string[];
    for (const line of labelLines) {
      y += lh(size);
      doc.text(line, left + indent, y);
    }
    y += lh(size);
    doc.text(value, right, y, { align: 'right' });
  };

  const separator = (gap = 1.6) => {
    y += gap;
    doc.setDrawColor(...GRAY);
    doc.setLineWidth(0.2);
    doc.line(left, y, right, y);
    y += gap;
  };

  const space = (mm = 1.5) => {
    y += mm;
  };

  // -------------------------------------------------------------------------
  // CABEÇALHO — identidade do emitente
  // -------------------------------------------------------------------------
  // Sem LOGO de propósito: a impressora térmica é de 1 bit e uma logo colorida
  // vira um borrão preto. O nome em negrito lê melhor e nunca falha.
  const company = data.company;
  writeCentered(company?.name || 'Minha Empresa', narrow ? 9 : 10, 'bold');
  if (company?.cnpj) writeCentered(`CNPJ/CPF: ${company.cnpj}`, 6.5, 'normal', GRAY);
  if (company?.address) writeCentered(company.address, 6.5, 'normal', GRAY);
  const contact = [company?.phone, company?.email].filter(Boolean).join(' · ');
  if (contact) writeCentered(contact, 6.5, 'normal', GRAY);

  separator();

  // -------------------------------------------------------------------------
  // TÍTULO DO DOCUMENTO + número + data
  // -------------------------------------------------------------------------
  writeCentered(resolveDocumentTitle(data), narrow ? 8.5 : 9.5, 'bold');
  const subtituloTermico = resolveDocumentSubtitle(data);
  if (subtituloTermico) writeCentered(subtituloTermico, 7, 'normal', GRAY);
  if (data.showSequenceId && data.sequenceId) {
    writeCentered(`Nº ${String(data.sequenceId).padStart(4, '0')}`, 7, 'bold', GRAY);
  }
  writeCentered(formatQuoteDate(data.quoteDate), 7, 'normal', GRAY);
  if (!isReceipt && data.validityDays && data.quoteDate) {
    writeCentered(
      `Válido até ${calcValidityDate(data.quoteDate, data.validityDays)}`,
      6.5,
      'normal',
      GRAY,
    );
  }
  if (!isReceipt && data.reference) writeCentered(`Ref.: ${data.reference}`, 6.5, 'normal', GRAY);

  separator();

  // -------------------------------------------------------------------------
  // CLIENTE
  // -------------------------------------------------------------------------
  writeLeft(`Cliente: ${data.client?.name?.trim() || 'Não informado'}`, 7);
  const clientDoc = clientDocumentLabel(data.client?.document);
  if (clientDoc) writeLeft(clientDoc, 6.5, 'normal', GRAY);
  if (data.client?.phone) writeLeft(`Tel.: ${data.client.phone}`, 6.5, 'normal', GRAY);
  if (data.client?.address) writeLeft(data.client.address, 6.5, 'normal', GRAY);

  separator();

  // -------------------------------------------------------------------------
  // CORPO — recibo tem forma própria; os demais são lista de itens + totais
  // -------------------------------------------------------------------------
  // As três funções abaixo avançam o `y` do closure pelas mesmas primitivas —
  // não devolvem nada de propósito, para não sugerir que são puras.
  if (isReceipt) {
    renderReceiptBody();
  } else {
    renderItemsBody();
  }

  // -------------------------------------------------------------------------
  // PAGAMENTO (o recibo tem o campo próprio dele, no corpo)
  // -------------------------------------------------------------------------
  const methods = isReceipt ? '' : formatPaymentMethods(data.paymentMethods);
  const terms = isReceipt ? '' : (data.paymentTerms || '').trim();
  if (methods || terms) {
    separator();
    if (methods) writeLeft(`Formas aceitas: ${methods}`, 6.5, 'normal', GRAY);
    if (terms) writeLeft(`Condições: ${terms}`, 6.5, 'normal', GRAY);
  }

  // -------------------------------------------------------------------------
  // EQUIPAMENTO / RESPONSÁVEIS (só a Ordem de Serviço preenche)
  // -------------------------------------------------------------------------
  // A comanda de bancada é justamente onde o equipamento importa: é o papel que
  // fica preso ao aparelho na prateleira. Sem número de série e acessórios, dois
  // aparelhos iguais viram um problema de identificação na entrega.
  const asset = assetEntries(data.serviceOrderAsset);
  if (asset.length) {
    separator();
    writeLeft('EQUIPAMENTO / OBJETO', 7, 'bold');
    for (const entry of asset) {
      space(0.8);
      writeLeft(entry.label, 6.5, 'bold', GRAY);
      writeLeft(entry.text, 6.5);
    }
  }

  const responsibles = serviceOrderInfoEntries(data);
  if (responsibles.length) {
    separator();
    writeLeft('RESPONSÁVEIS E PRAZOS', 7, 'bold');
    for (const entry of responsibles) {
      writeLeft(`${entry.label}: ${entry.text}`, 6.5, 'normal', GRAY);
    }
  }

  // -------------------------------------------------------------------------
  // DIAGNÓSTICO TÉCNICO (só a Ordem de Serviço preenche)
  // -------------------------------------------------------------------------
  const diagnosis = diagnosisEntries(data.diagnosis);
  if (diagnosis.length) {
    separator();
    writeLeft('DIAGNÓSTICO TÉCNICO', 7, 'bold');
    for (const entry of diagnosis) {
      space(0.8);
      writeLeft(entry.label, 6.5, 'bold', GRAY);
      writeLeft(entry.text, 6.5);
    }
  }

  // -------------------------------------------------------------------------
  // QR DO PIX
  // -------------------------------------------------------------------------
  // QR é 1 bit por natureza — é o único elemento gráfico que a térmica imprime
  // bem. O tamanho é generoso de propósito: a impressora vai limiarizar a
  // imagem, e um QR pequeno perde módulos e para de ler.
  if (opts.pixQrDataUrl) {
    separator();
    const qrSize = Math.min(contentWidth - 6, narrow ? 34 : 40);
    space(1);
    doc.addImage(opts.pixQrDataUrl, 'PNG', center - qrSize / 2, y, qrSize, qrSize);
    y += qrSize;
    space(1);
    writeCentered('Pague com PIX', 7, 'bold');
    // O valor do QR é o total CALCULADO (mesma regra do A4): se divergir, o
    // cliente escaneia e paga um número diferente do que o cupom diz.
    const pixAmount = pdfQuoteTotals(data).total;
    if (pixAmount > 0) writeCentered(formatCurrency(pixAmount), 7.5, 'bold');
  }

  // -------------------------------------------------------------------------
  // OBSERVAÇÕES
  // -------------------------------------------------------------------------
  const notes: Array<[string, string]> = [];
  // Campos extras do prestador (L5). Entram ANTES da garantia porque são
  // identificação do serviço ("Placa do veículo"), não condição comercial.
  if (!isReceipt) {
    for (const extra of extraFieldEntries(data)) notes.push([extra.label, extra.value]);
  }
  if (!isReceipt && data.warranty) notes.push(['Garantia', data.warranty]);
  if (!isReceipt && data.notes) notes.push(['Obs.', data.notes]);
  if (!isReceipt && data.termsConditions) notes.push(['Termos', data.termsConditions]);
  if (notes.length) {
    separator();
    for (const [label, text] of notes) {
      writeLeft(`${label}: ${text}`, 6.5, 'normal', GRAY);
      space(0.6);
    }
  }

  // -------------------------------------------------------------------------
  // ASSINATURA — LINHA para assinar à caneta, nunca a imagem salva
  // -------------------------------------------------------------------------
  // No balcão o papel está na mão da pessoa: a assinatura de verdade é a que
  // ela faz agora. Imprimir a imagem salva da assinatura numa via que o cliente
  // leva embora é pior (1 bit borra o traço e ninguém assina nada).
  renderSignatureLine();

  // -------------------------------------------------------------------------
  // RODAPÉ — mesma regra de branding do A4
  // -------------------------------------------------------------------------
  space(3);
  if (!data.isPremium) {
    writeCentered('Gerado por Orçamigo', 6, 'normal', GRAY);
    writeCentered('orcamigo.com.br', 6, 'normal', GRAY);
  } else {
    writeCentered(company?.name || '', 6, 'normal', GRAY);
  }

  return y;

  // =========================================================================
  // Corpos
  // =========================================================================

  function renderItemsBody(): void {
    const items = data.items || [];
    const totals = pdfQuoteTotals(data);

    writeLeft('ITENS', 7, 'bold');
    space(0.8);

    for (const item of items) {
      // Nome em linha própria e QUEBRADO, nunca truncado: na bobina o papel é
      // infinito para baixo, então cortar o nome (que é o que identifica a
      // variação P/M/G do produto) só perderia informação de graça.
      writeLeft(item.name || '-', 7);

      const unit = unitShortLabel(item.unit);
      const qty = formatQuantityDisplay(item.quantity);
      const mathLabel = `${qty}${unit ? ` ${unit}` : ''} x ${formatCurrency(item.unit_price)}`;
      writeRow(mathLabel, formatCurrency(quoteItemLineTotal(item)), 6.5, 'normal', BLACK, 2);

      const discount = item.discount || 0;
      if (discount > 0) {
        const label =
          item.discount_type === 'percent'
            ? `desconto ${discount}%`
            : `desconto ${formatCurrency(discount)}`;
        writeLeft(label, 6, 'normal', GRAY, 2);
      }
      space(0.8);
    }

    separator();

    // Decomposição só quando há o que decompor — no cupom simples (a maioria)
    // sai a linha de TOTAL sozinha, como deve ser.
    if (totals.descontoItens > 0 || totals.descontoCabecalho > 0 || totals.frete > 0) {
      writeRow('Subtotal', formatCurrency(totals.subtotal), 6.5, 'normal', GRAY);
      if (totals.descontoItens > 0) {
        writeRow('Descontos nos itens', `- ${formatCurrency(totals.descontoItens)}`, 6.5, 'normal', GRAY);
      }
      if (totals.descontoCabecalho > 0) {
        writeRow('Desconto no total', `- ${formatCurrency(totals.descontoCabecalho)}`, 6.5, 'normal', GRAY);
      }
      if (totals.frete > 0) {
        writeRow('Frete', `+ ${formatCurrency(totals.frete)}`, 6.5, 'normal', GRAY);
      }
      space(1);
    }

    writeRow('TOTAL', formatCurrency(totals.total), narrow ? 10 : 11, 'bold');
  }

  function renderReceiptBody(): void {
    const amount = Number(data.total ?? 0);

    writeCentered('VALOR RECEBIDO', 6.5, 'normal', GRAY);
    writeCentered(formatCurrency(amount), narrow ? 13 : 15, 'bold');
    space(1.5);

    const payer = data.client?.name?.trim() || 'Não informado';
    const descricao = data.receiptDescription?.trim() || '—';
    writeLeft(
      `Recebi(emos) de ${payer} a importância de ${numeroParaReais(amount)} ` +
        `(${formatCurrency(amount)}), referente a ${descricao}.`,
      6.5,
    );
    space(1);

    if (data.receiptPaymentMethodLabel?.trim()) {
      writeLeft(`Forma de pagamento: ${data.receiptPaymentMethodLabel.trim()}`, 6.5, 'bold');
    }
    if (data.receiptNotes?.trim()) {
      space(0.8);
      writeLeft(`Obs.: ${data.receiptNotes.trim()}`, 6, 'normal', GRAY);
    }

    space(1);
    const place = data.receiptIssuePlace?.trim();
    const dateStr = formatQuoteDate(data.quoteDate);
    writeCentered(place ? `${place}, ${dateStr}.` : `${dateStr}.`, 6.5, 'normal', GRAY);
  }

  function renderSignatureLine(): void {
    // Mesma regra do A4, pela mesma função: no PDV a linha do cliente é
    // configurável. No cupom só existe essa linha, então desligada não sobra
    // bloco nenhum — e o cupom fecha logo depois do total/QR, que é o que o
    // balcão quer.
    if (!showsPdvClientSignature(data)) return;
    space(8);
    const lineWidth = Math.min(contentWidth, narrow ? 42 : 56);
    doc.setDrawColor(...BLACK);
    doc.setLineWidth(0.25);
    doc.line(center - lineWidth / 2, y, center + lineWidth / 2, y);

    // O recibo é assinado por quem RECEBE (o emitente); orçamento, pedido e OS
    // são assinados por quem aceita (o cliente).
    const name = isReceipt
      ? data.issuerSignerName?.trim() || company?.name?.trim() || ''
      : data.clientSignerName?.trim() || data.client?.name?.trim() || '';
    const role = isReceipt ? 'Emitente' : 'Assinatura do cliente';

    if (name) writeCentered(name, 6.5, 'bold');
    writeCentered(role, 6, 'normal', GRAY);
  }
}
